import React from 'react';
import { niceYAxis } from '../../utils/chartAxisUtils';

export default function FitnessProfileBloodPressureLineChart({
	data = [],
	trendLine = [],
	bloodPressureGoal = null
}) {
	const chartValues = [
		...data.flatMap((e) => [e.systolic, e.diastolic]),
		...trendLine.flatMap((e) => [e.systolic, e.diastolic])
	].filter((v) => Number.isFinite(v));

	if (!chartValues.length) {
		return (
			<p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No blood pressure data</p>
		);
	}

	const dataMin = Math.min(...chartValues);
	const dataMax = Math.max(...chartValues);
	const span = dataMax - dataMin;
	const pad = span === 0 ? 5 : Math.max(2, span * 0.08);
	let paddedMin = dataMin - pad;
	let paddedMax = dataMax + pad;
	if (paddedMax - paddedMin < 14) {
		const mid = (dataMin + dataMax) / 2;
		paddedMin = mid - 7;
		paddedMax = mid + 7;
	}
	const { axisMin: min, axisMax: max, range, ticks: yTicks } = niceYAxis(paddedMin, paddedMax);
	const pointCount = Math.max(data.length, 1);

	const toY = (value) => 100 - ((value - min) / range) * 100;
	const toX = (index) => (index / Math.max(data.length - 1, 1)) * 100;

	const toLoggedPolyline = (key) =>
		data
			.map((entry, index) => {
				if (!Number.isFinite(entry[key])) return null;
				return `${toX(index)},${toY(entry[key])}`;
			})
			.filter(Boolean)
			.join(' ');

	const toTrendPolyline = (key) =>
		trendLine
			.map((entry) => {
				const dataIndex = data.findIndex((point) => point.date === entry.date);
				if (dataIndex < 0 || !Number.isFinite(entry[key])) return null;
				return `${toX(dataIndex)},${toY(entry[key])}`;
			})
			.filter(Boolean)
			.join(' ');

	const loggedSystolic = toLoggedPolyline('systolic');
	const loggedDiastolic = toLoggedPolyline('diastolic');
	const trendSystolic = toTrendPolyline('systolic');
	const trendDiastolic = toTrendPolyline('diastolic');

	return (
		<div className="mt-4">
			<div className="flex gap-3">
				<div className="w-11 shrink-0 h-48 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 text-right">
					{yTicks.map((tick, index) => (
						<span key={`${tick}-${index}`}>{tick}</span>
					))}
				</div>
				<svg viewBox="0 0 100 100" className="flex-1 h-48 overflow-visible">
					{trendSystolic && (
						<polyline
							fill="none"
							stroke="#f87171"
							strokeWidth="2"
							strokeDasharray="6 4"
							points={trendSystolic}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{trendDiastolic && (
						<polyline
							fill="none"
							stroke="#818cf8"
							strokeWidth="2"
							strokeDasharray="6 4"
							points={trendDiastolic}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{loggedSystolic && (
						<polyline
							fill="none"
							stroke="#dc2626"
							strokeWidth="2"
							points={loggedSystolic}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{loggedDiastolic && (
						<polyline
							fill="none"
							stroke="#4f46e5"
							strokeWidth="2"
							points={loggedDiastolic}
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{data.map((entry, index) => {
						const x = toX(index);
						return (
							<g key={entry.date || `${entry.month}-${index}`}>
								{Number.isFinite(entry.systolic) && (
									<circle
										cx={x}
										cy={toY(entry.systolic)}
										r="2.3"
										fill="#dc2626"
										vectorEffect="non-scaling-stroke"
									/>
								)}
								{Number.isFinite(entry.diastolic) && (
									<circle
										cx={x}
										cy={toY(entry.diastolic)}
										r="2.3"
										fill="#4f46e5"
										vectorEffect="non-scaling-stroke"
									/>
								)}
							</g>
						);
					})}
				</svg>
			</div>
			<div
				className="mt-1 grid text-xs text-gray-500 dark:text-gray-400"
				style={{ gridTemplateColumns: `repeat(${pointCount}, minmax(0, 1fr))` }}
			>
				{data.map((entry, index) => (
					<span key={entry.date || `${entry.month}-${index}`} className="text-center">
						{entry.month}
					</span>
				))}
			</div>
			<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-400">
				{bloodPressureGoal && trendLine.length > 0 && (
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-0.5 w-5 border-t-2 border-dashed border-slate-400" />
						Goal pace ({bloodPressureGoal.target_metric})
					</div>
				)}
				<div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
					<span className="inline-block h-2 w-2 rounded-full bg-red-600" />
					Systolic
				</div>
				<div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
					<span className="inline-block h-2 w-2 rounded-full bg-indigo-600" />
					Diastolic
				</div>
			</div>
		</div>
	);
}
